import { createColumnHelper } from '@tanstack/react-table';
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
  subDays,
} from 'date-fns';
import { TFunction } from 'i18next';
import { FC } from 'react';
import Tooltip from 'react-tooltip-lite';
import {
  formatedDate,
  formatNumericValue,
  getFixedNumber,
} from 'utils/general';

import { ISettlements } from '@/modules/Settlements/types';
import { Typography } from '@/shared/components/atoms';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';

// TODO: currency
export const getCardData = ({
  data,
  currency,
  language,
}: {
  data: ISettlements.ISettlementDetailsDayBasic;
  currency: string;
  language: string;
}) => {
  const {
    day,
    attendance,
    totalMainPayable,
    totalSupervisionDeductions,
    totalAdministrativeDeduction,
    totalSupervisionBonuses,
    totalHoursBonuses,
    totalAdministrativeBonuses,
    totalNetPayment,
  } = data;
  return {
    date: formatedDate(
      day,
      `eeee ${language === 'ar' ? 'yyyy/MM/d' : 'd/MM/yyyy'}`,

      language
    ),
    attendanceNumber: attendance || '-',

    'main-dues': totalMainPayable
      ? `${formatNumericValue(totalMainPayable)} ${currency || ''}`
      : '-',

    supervisionDeduction: totalSupervisionDeductions
      ? `${formatNumericValue(totalSupervisionDeductions)} ${currency || ''}`
      : '-',
    managerialDeduction: totalAdministrativeDeduction
      ? `${formatNumericValue(totalAdministrativeDeduction)} ${currency || ''}`
      : '-',
    'extra-supervision': totalSupervisionBonuses
      ? `${formatNumericValue(totalSupervisionBonuses)} ${currency || ''}`
      : '-',

    'extra-hours': totalHoursBonuses
      ? `${formatNumericValue(totalHoursBonuses)} ${currency || ''}`
      : '-',
    administrativeIncentives: totalAdministrativeBonuses
      ? `${formatNumericValue(totalAdministrativeBonuses)} ${currency || ''}`
      : '-',

    'total-dues': totalNetPayment
      ? `${formatNumericValue(totalNetPayment)} ${currency || ''}`
      : '-',
  };
};

//**************************************table***************************************** */
interface OverTimeProps {
  overTimeDetails: ISettlements.overTimeDetailsItem[];
  t: TFunction<'common', undefined>;
  currency: string;
}
const OverTimeDetailsContent: FC<OverTimeProps> = ({
  overTimeDetails,
  t,
  currency,
}) => {
  const { pricePerHour, totalHours } = overTimeDetails[0];
  return (
    <div className=' rounded-lg bg-background-200 px-16 py-4 text-center'>
      <Typography variant='body2'>
        {t('the-first-duration-is-calculated-x', {
          totalHours: formatNumericValue(totalHours),
          pricePerHour: formatNumericValue(pricePerHour),
          currency: currency,
        })}
      </Typography>

      {overTimeDetails.slice(1).map(({ pricePerHour, totalHours }, index) => (
        <Typography key={index} variant='body2'>
          {t('and')} {t('duration')} {t((index + 2).toString())} ({totalHours}{' '}
          {t('hour')}) {t('with')} {pricePerHour} {currency}
        </Typography>
      ))}
    </div>
  );
};

const getRenderedCellValue = (value: string | number | undefined | null) => (
  <Typography
    variant='subtitle2'
    className=' max-w-[9.25rem] overflow-clip truncate text-ellipsis whitespace-nowrap'
  >
    {/* check if number and float make fixed 2 else show value oring with (-) */}
    {typeof value === 'number' ? formatNumericValue(value) : value || '-'}
  </Typography>
);

const columnHelper =
  createColumnHelper<ISettlements.ISettlementDetailsDayInstance>();

export const settlementsInstancesTableColumns = (
  t: TFunction<'common', undefined>,
  currency: string
) => {
  const column = [];

  column.push(
    columnHelper.accessor('jobInstanceId', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('job-order'),
    })
  );

  column.push(
    columnHelper.accessor('totalWorkers', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('attendance'),
    })
  );
  column.push(
    columnHelper.accessor('place', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('location'),
    })
  );
  column.push(
    columnHelper.accessor('supervisorName', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('supervisorName'),
    })
  );
  column.push(
    columnHelper.accessor('jobInstanceName', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('executed-job'),
    })
  );
  // column.push(
  //   columnHelper.accessor('cropName', {
  //     cell: ({ getValue }) => getRenderedCellValue(getValue()),
  //     header: () => t('the-type2'),
  //   })
  // );

  column.push(
    columnHelper.accessor('supplierAssignmentId', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('contract-code'),
    })
  );

  column.push(
    columnHelper.accessor('assignmentJobName', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('contract-job'),
    })
  );
  column.push(
    columnHelper.accessor('pricingMethod', {
      cell: ({ getValue }) =>
        getRenderedCellValue(t(getValue() === 'unit' ? 'rate' : getValue())),
      header: () => t('contract-form'),
    })
  );

  column.push(
    columnHelper.accessor('payUnit', {
      cell: ({ getValue }) =>
        getRenderedCellValue(getValue() === 'daily' ? t('daily') : getValue()),
      header: () => t('pay-unit'),
    })
  );
  column.push(
    columnHelper.accessor('suppAssPrice', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => (
        <>
          {t('pay-unit-value')} ({currency})
        </>
      ),
    })
  );

  column.push(
    columnHelper.accessor('unitType', {
      cell: ({
        getValue,
        row: {
          original: { category },
        },
      }) =>
        category === 'WASTED-TIME' ? '-' : getRenderedCellValue(getValue()),
      header: () => t('productivity-unit'),
    })
  );
  column.push(
    columnHelper.accessor('expectedProductivityTarget', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('productivity-target-per-worker'),
    })
  );
  column.push(
    columnHelper.accessor('totalUnitsAchieved', {
      cell: ({
        getValue,
        row: {
          original: { unitType, category },
        },
      }) =>
        category === 'WASTED-TIME' ? (
          '-'
        ) : (
          <>
            {getRenderedCellValue(getValue())} {unitType}
          </>
        ),
      header: () => t('actual-productivity-per-team'),
    })
  );
  column.push(
    columnHelper.accessor('supervisionBonuses', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => (
        <>
          {t('supervisionBonuses')} ({currency})
        </>
      ),
    })
  );
  column.push(
    columnHelper.accessor('supervisionDeduction', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => (
        <>
          {t('supervisionDeduction')} ({currency})
        </>
      ),
    })
  );
  column.push(
    columnHelper.accessor('numberHoursBonuses', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => t('number-extra-hours'),
    })
  );

  column.push(
    columnHelper.accessor('totalHoursBonuses', {
      header: () => (
        <>
          {t('value-extra-hours')} ({currency})
        </>
      ),
      // cell: ({ getValue }) => <Typography variant='subtitle2'   className=' overflow-clip text-ellipsis whitespace-nowrap'>{getRenderedCellValue(getValue())}</Typography>,
      cell: ({
        getValue,
        row: {
          original: { overTimeDetails },
        },
      }) =>
        getValue() && overTimeDetails.length > 0 ? (
          <Tooltip
            content={
              <OverTimeDetailsContent
                overTimeDetails={
                  overTimeDetails as ISettlements.overTimeDetailsItem[]
                }
                t={t}
                currency={currency}
              />
            }
            direction='down'
            arrow={false}
            tipContentHover
          >
            <div className='flex items-center justify-center gap-2 '>
              {getRenderedCellValue(getValue())}

              <ExleminationMark />
            </div>
          </Tooltip>
        ) : (
          getRenderedCellValue(getValue())
        ),
    })
  );
  column.push(
    columnHelper.accessor('averagePayUnitsPerWorker', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => (
        <div className='flex items-center gap-2'>
          {t('average-pay-unit-per-worker')}

          <Tooltip
            content={
              <Typography className='w-fit min-w-[200px] max-w-[400px] rounded-lg bg-background-200 px-16 py-4 text-center'>
                {t('average-pay-unit-per-worker-tool-tip-info')}
              </Typography>
            }
            direction='down'
            arrow={false}
            tipContentHover
          >
            <div className='flex items-center justify-center gap-2 '>
              <ExleminationMark />
            </div>
          </Tooltip>
        </div>
      ),
    })
  );
  column.push(
    columnHelper.accessor('totalPayUnits', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => (
        <div className='flex items-center gap-2'>
          {t('total-number-pay-units')}
          <Tooltip
            content={
              <Typography className='w-fit min-w-[200px] max-w-[400px] rounded-lg bg-background-200 px-16 py-4 text-center'>
                {t('total-number-pay-units-tool-tip-info')}
              </Typography>
            }
            direction='down'
            arrow={false}
            tipContentHover
          >
            <div className='flex items-center justify-center gap-2 '>
              <ExleminationMark />
            </div>
          </Tooltip>
        </div>
      ),
    })
  );

  column.push(
    columnHelper.accessor('netPayment', {
      cell: ({ getValue }) => (
        <Typography variant='subtitle2' weight='bold'>
          {getFixedNumber(getValue())}
        </Typography>
      ),

      header: () => (
        <>
          {t('total-dues')} ({currency})
        </>
      ),
    })
  );
  column.push(
    columnHelper.accessor('uploadDate', {
      cell: ({ getValue }) =>
        getRenderedCellValue(
          getValue() && format(new Date(getValue()), 'dd/MM/yyyy')
        ),

      header: () => t('upload-date'),
    })
  );
  return column;
};

type WorkerSettlementDayWithEmpty = ISettlements.IWorkerSettlementDaySchema & {
  embtyDay?: Date;
};

export const getWorkerSerializedPeriodDaysArr = (
  days: ISettlements.IWorkerSettlementDaySchema[],
  startDate: string,
  endDate: string
): WorkerSettlementDayWithEmpty[] => {
  const dayMap = new Map(
    days.map((day) => [parseISO(day.date).toISOString(), day])
  );
  const result: WorkerSettlementDayWithEmpty[] = [];

  const current = parseISO(startDate);
  const end = parseISO(endDate);

  while (current <= end) {
    const dateIso = current.toISOString();
    const existingDay = dayMap.get(dateIso);

    result.push(
      (existingDay as WorkerSettlementDayWithEmpty) ?? {
        date: dateIso,
        totalPay: 0,
        basePay: 0,
        totalBonuses: 0,
        totalDeductions: 0,
        totalHours: 0,
        totalOperations: 0,
        totalOvertime: 0,
        embtyDay: new Date(current),
      }
    );

    current.setUTCDate(current.getUTCDate() + 1);
  }

  return result;
};

export const getSerializedPeriodDaysArr = <
  T extends { day: string; embtyDay?: Date }
>(
  days: T[],
  startDate?: string,
  endDate?: string
) => {
  const periodDays = differenceInCalendarDays(
    new Date(endDate || ''),
    new Date(startDate || '')
  );

  const newPeriodDaysArr: T[] = [];
  let current = new Date(endDate || '');
  let currentDaysIndex = 0;

  // loop over period and fill array with two types of records
  // 1 - if current period day === to curent backend record then push it to array
  // 2 - else if this day not returnd from backend then create new record that indicat that day is embty and push it

  for (let i = 0; i <= periodDays; i++) {
    if (isSameDay(current, new Date(days[currentDaysIndex]?.day || ''))) {
      newPeriodDaysArr.push(days[currentDaysIndex]);
      currentDaysIndex++;
    } else {
      // just add new att embtyDate to any day record ex day[0]
      newPeriodDaysArr.push({
        ...days[0],
        embtyDay: current,
      });
    }
    current = subDays(new Date(current), 1);
  }
  return newPeriodDaysArr;
};
